import { CartQueryShippingAddress } from '@app/types/monolith/types';
import type { IntlShape } from '@redbubble/next/intl';
import logger from '@redbubble/next/logger';
import type {
  PageType,
  PayPalCheckout,
  PayPalCheckoutUpdatePaymentOptions,
  PayPalCheckoutUpdatePaymentServerDataReturn,
  PayPalRequiredCart,
  ShippingAddressChangeData,
} from '../types';
import {
  subtotalAfterCouponApplied,
  toPaypalLineItems,
  toPaypalShippingOptions,
} from './utils';

export interface HandleOnShippingAddressChange {
  data: ShippingAddressChangeData;
  paypalClient: PayPalCheckout;
  onShippingAddressChange: (
    shippingAddress: CartQueryShippingAddress
  ) => Promise<PayPalRequiredCart | undefined>;
  currency: string;
  intl: IntlShape;
  onZeroPayment: () => void;
  page: PageType;
}

export const handleOnShippingAddressChange = async ({
  data,
  paypalClient,
  onShippingAddressChange,
  currency,
  intl,
  onZeroPayment,
  page,
}: HandleOnShippingAddressChange): Promise<
  undefined | PayPalCheckoutUpdatePaymentServerDataReturn
> => {
  const { countryCode, postalCode, state } = data.shippingAddress;
  const cart = await onShippingAddressChange({
    countryCode,
    postcode: postalCode,
    state,
  }).catch((error) => {
    const message = `Failed to refresh cart after shipping address change: ${error.message}`;
    logger.error(
      {
        err: error,
        context: 'PayPalExpress/handlers/handleOnShippingAddressChange',
        message,
      },
      message
    );
  });

  if (!cart) {
    throw new Error('cart is not defined');
  }

  const {
    summary: cartSummary,
    items: cartLineItems,
    applied_coupon: cartCoupon,
  } = cart;

  if (cartSummary.total === 0) {
    onZeroPayment();
    return Promise.resolve(undefined);
  }

  const subtotalAfterCoupon = subtotalAfterCouponApplied({
    subtotal: cartSummary.subtotal,
    coupon: cartCoupon,
    intl,
  });

  /* When the discount from coupon bleeds into shipping, we don't want to show shipping options in a dropdown.
   * This is because we can't offset the individual shipping option amount by the "bleed amount". For example,
   * if $2 bleeds into the shipping, we can't offset each shipping option by $2 as there is no way to split the $2
   * between the RB cost and tax.
   *
   * If in this case we were to send the shipping options, PayPal would return a HTTP/422 error as it
   * would notice that "line items + shipping > order total" by an amount <= shipping tax.
   */
  const showShippingMethodAsLineItem = subtotalAfterCoupon <= 0;

  const allLineItems = toPaypalLineItems({
    cartLineItems,
    cartCoupon,
    cartSummary,
    showShippingMethodAsLineItem,
    intl,
    isCreateOrder: false,
    page,
  });

  const updatePaymentOptions: PayPalCheckoutUpdatePaymentOptions = {
    paymentId: data.orderID,
    amount: cartSummary.total,
    currency,
    lineItems: allLineItems,
    shippingOptions: !showShippingMethodAsLineItem
      ? toPaypalShippingOptions({
          estimatedShipping: cartSummary.estimated_shipping,
          currency,
          intl,
        })
      : undefined,
  };

  return paypalClient.updatePayment(updatePaymentOptions);
};
